import { useNavigate } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import type { CultureUniverse } from '@/mocks/culture';

interface UniverseSectionProps {
  universe: CultureUniverse;
  index: number;
}

function UniverseSection({ universe, index }: UniverseSectionProps) {
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollReveal(0.1);
  const reversed = index % 2 === 1;

  return (
    <section
      id={universe.id}
      className={`py-16 md:py-24 ${index % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}`}
    >
      <div
        ref={ref}
        className={`w-full px-4 md:px-8 lg:px-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Header with icon + title */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-center">
          {/* Image */}
          <div
            className={`w-full lg:w-1/2 ${
              reversed ? 'lg:order-2' : ''
            }`}
          >
            <div className="relative h-64 md:h-96 overflow-hidden rounded-xl border border-background-200/70">
              <img
                src={universe.heroImage}
                alt={universe.title}
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-3">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-background-50/90 backdrop-blur-sm border border-background-200/70">
                  <i className={`${universe.icon} text-xl text-accent-400`} />
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className={`w-full lg:w-1/2 ${reversed ? 'lg:order-1' : ''}`}>
            <span className="inline-block text-xs font-semibold text-accent-400 uppercase tracking-wider mb-3">
              {universe.tagline}
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              {universe.title}
            </h2>
            <p className="text-base text-foreground-400 leading-relaxed mb-6">
              {universe.description}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {universe.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg bg-background-100 border border-background-200/70 px-4 py-3 text-center"
                >
                  <div className="font-heading font-bold text-xl md:text-2xl text-accent-500">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap">
              {universe.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Highlights */}
        <div className="mt-10 md:mt-14">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground-50">
              À la une dans cet univers
            </h3>
            <button
              onClick={() =>
                navigate(`/directory?q=${encodeURIComponent(universe.tags[0] || universe.title)}`)
              }
              className="flex items-center gap-1.5 text-sm font-medium text-accent-400 hover:gap-2.5 transition-all whitespace-nowrap"
            >
              <span>Tout découvrir</span>
              <i className="ri-arrow-right-line" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {universe.highlights.map((highlight) => (
              <button
                key={highlight.name}
                onClick={() =>
                  navigate(`/directory?q=${encodeURIComponent(highlight.name)}`)
                }
                className="group text-left rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-400/40 overflow-hidden transition-all duration-300"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={highlight.image}
                    alt={highlight.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-foreground-50 mb-1">{highlight.name}</h4>
                  <div className="flex items-center gap-1.5 text-xs text-foreground-400 mb-2">
                    <i className="ri-map-pin-line text-accent-400" />
                    <span>{highlight.location}</span>
                  </div>
                  <p className="text-sm text-foreground-400 leading-relaxed">
                    {highlight.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default UniverseSection;